import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ApiService from '../../services/ApiService';
import { modal } from '../modal/ModalEmitter';
import QuoteDocumentExpressPreview from './QuoteDocumentExpressPreview';
import _ from 'lodash';
import { Dropdown } from 'react-bootstrap';
import FileService from '../../services/FileService';
import PdfObject from '../PdfObject';
import LoadingPage from '../LoadingPage';
import FlexAlert from '../FlexAlert';
import { getDisplayName } from '../../util/user';

const defaultIcon = 'far fa-file-alt';

const QuoteDocuments = ({ quoteId }) => {
  const [quote, setQuote] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [archive, setArchive] = useState(null);

  const { users } = useSelector(state => state.chat);

  const openSupplemental = (templateId, options) => {
    const template = templates.find(t => t.id === templateId);

    if (!template) {
      return;
    }

    modal.open('quote-preview', {
      hidePrint: true,
      hideFooter: true,
      // dialogClass: 'full', // we need route history with full
      title: (
        <>
          <i className={template.icon || defaultIcon} /> {template.name} (Q{quoteId})
        </>
      ),
      message: <QuoteDocumentExpressPreview quoteId={quoteId} templateId={templateId} options={options} />,
    });
  };

  const refreshQuote = () => {
    ApiService.get(`db/EQS1/${quoteId}`).then(response => setQuote(response.data));
  };

  const handleTemplateClick = async (template, options) => openSupplemental(template.id, options);

  const fetchTemplates = () => {
    return ApiService.get('crm/templates').then(response => setTemplates(response.data));
  };

  const fetchArchive = () => {
    return ApiService.get(`crm/quote/${quoteId}/archive`).then(response => {
      // key by template
      const docs = {};
      for (const doc of response.data || []) {
        if (!docs[doc.templateId]) {
          docs[doc.templateId] = [];
        }
        docs[doc.templateId].push(doc);
      }

      setArchive(docs);
    });
  };

  const viewDocument = ({ filePath }) => {
    ApiService.get('archive', {
      params: {
        filePath,
      },
    }).then(res => {
      modal.info({
        title: 'Document',
        hidePrint: false,
        // handlePrint: () => this.download(obj._id, obj.signed),
        message: <PdfObject url={FileService.downloadPath(res.data.fileName)} width="100%" height="800" />,
      });
    });
  };

  const getArchiveButton = templateId => {
    if (!archive[templateId]) {
      return;
    }

    return (
      <Dropdown>
        <Dropdown.Toggle menuAlign="right" variant="secondary">
          Archive ({archive[templateId].length})
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {archive[templateId].map((doc, i) => {
            return (
              <Dropdown.Item key={i} eventKey={i} onClick={() => viewDocument(doc)}>
                {doc.createdAt}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  useEffect(() => {
    refreshQuote();
    fetchTemplates();
    fetchArchive();
  }, []);

  if (!templates || !archive) {
    return <LoadingPage />;
  }

  const renderTeamTemplate = template => {
    const icon = template.icon || defaultIcon;
    const commissionUsers = _.get(quote, 'data.commission.user', []);

    return (
      <React.Fragment key={template.id}>
        <div key={template.id} className="document-item">
          <div className="d-flex flex-grow-1">
            <div className="document-icon">
              <i className={icon} />
            </div>
            <div className="document-name">{template.label || template.name}</div>
          </div>
        </div>
        {commissionUsers.map(userId => {
          const user = users.find(u => u.id === userId);
          return (
            <div key={user.id} className="team-document-item" onClick={() => handleTemplateClick(template, { userId })}>
              {getDisplayName(user)}
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  const renderTemplates = () => {
    if (!templates) {
      return;
    }

    if (!templates.length) {
      return <FlexAlert>Sales Forms Not Found</FlexAlert>;
    }

    return templates.map(template => {
      if (template.name === 'DealAnalysis') {
        return renderTeamTemplate(template);
      }

      const icon = template.icon || defaultIcon;
      return (
        <div key={template.id} className="document-item">
          <div className="d-flex flex-grow-1" onClick={() => handleTemplateClick(template)}>
            <div className="document-icon">
              <i className={icon} />
            </div>
            <div className="document-name">{template.label || template.name}</div>
          </div>
          <div style={{ textAlign: 'right' }}>{getArchiveButton(template.id)}</div>
        </div>
      );
    });
  };

  return (
    <div className="quote-documents">
      <div className="document-list">{renderTemplates()}</div>
    </div>
  );
};

QuoteDocuments.propTypes = {
  quoteId: PropTypes.number,
};

export default QuoteDocuments;
